import { useMemo, useCallback } from "react";
import { useKarateUi } from "@/hooks/useKarateUi.js";
import { getBackendToken } from "@/api/karateBackendApi.js";
import {
  apiCreateDojo,
  apiUpdateDojo,
  apiDeactivateDojo,
  apiCreateStudent,
  apiUpdateStudent,
  apiDeactivateStudent,
  apiCreateRoleAssignment,
  apiUpdateRoleAssignment,
  apiDeactivateRoleAssignment,
  apiErrorMessage,
} from "@/api/backendCrud051.js";

export function useBackendCrudActions({ backendStatus, dataSource, refreshBackend }) {
  const { ui, locale } = useKarateUi();
  const enabled = dataSource === "backend" && backendStatus === "backend" && Boolean(getBackendToken());

  const formatError = useCallback(
    (err) => apiErrorMessage(err, { ...ui, locale }),
    [ui, locale],
  );

  const onRefresh = useCallback(async () => {
    await refreshBackend?.();
  }, [refreshBackend]);

  const dojosCrud = useMemo(
    () =>
      enabled
        ? {
            enabled: true,
            formatError,
            onRefresh,
            onCreate: apiCreateDojo,
            onUpdate: apiUpdateDojo,
            onArchive: (id) => apiDeactivateDojo(id),
          }
        : null,
    [enabled, formatError, onRefresh],
  );

  const studentsCrud = useMemo(
    () =>
      enabled
        ? {
            enabled: true,
            formatError,
            onRefresh,
            onCreate: apiCreateStudent,
            onUpdate: apiUpdateStudent,
            onArchive: (id) => apiDeactivateStudent(id),
          }
        : null,
    [enabled, formatError, onRefresh],
  );

  const instructeursCrud = useMemo(
    () =>
      enabled
        ? {
            enabled: true,
            formatError,
            onRefresh,
            onCreate: apiCreateRoleAssignment,
            onUpdate: (id, item) => {
              const assignmentId = item.assignment_id || `ra-${id}`;
              return apiUpdateRoleAssignment(assignmentId, item);
            },
            onArchive: (_id, row) => {
              const assignmentId = row?.assignment_id || `ra-${_id}`;
              return apiDeactivateRoleAssignment(assignmentId);
            },
          }
        : null,
    [enabled, formatError, onRefresh],
  );

  return { enabled, dojosCrud, studentsCrud, instructeursCrud, formatError };
}
